



















































































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';
import { useStore } from '@/store';

// services
import { useService } from '@/vue3/api/services';

// components
import GridEditor from '@/vue3/components/GridEditor.vue';
import GeoRecordEditor from '@/vue3/components/GeoRecordEditor.vue';
import { Portal } from '@linusborg/vue-simple-portal';

// hooks
import { useGeoRecordDelete } from '@/vue3/hooks/geoRecordDeleteHook';
import { useNotify } from '@/vue3/hooks/notifyHook';
import { useGeoRecordPdf } from '@/vue3/hooks/geoRecordPdfHook';

// config
import tableColumns from '@/vue3/configs/table/geoRecordsConfig';

// models
import { GeoRecord } from '@/vue3/api/models';
import Button from 'primevue/button';

// primevue
import Galleria from 'primevue/galleria';

export default defineComponent({
    components: {
        GridEditor,
        Galleria,
        Portal,
        Button,
    },
    props: {
        mapbox: {
            type: Object,
            default: null,
        },
        refreshGeoRecords: {
            type: Function,
            default: () => {},
        },
        fetchArchiveEvents: {
            type: Function,
            default: () => {},
        },
        refershArchiveGeoRecordLayerTree: {
            type: Function,
            default: () => {},
        },
    },
    setup(props: any) {
        // store
        const store = useStore();
        const { apiUrl } = store.state;

        // hooks
        const { deleteGeoRecordLayer } = useGeoRecordDelete();
        const selectedColumns = ref(tableColumns);
        const filters = ref({});

        // services
        const {
            getAllGeoRecordSummary,
            getGeoRecordImagesById,
            getAllGeoRecordCategory,
        } = useService();
        const { warnToast, successToast, errorToast } = useNotify();
        const { generatePDf, isGeneratingPdf } = useGeoRecordPdf();

        // refs
        const isMeters = store.state.isMeters;
        const displayBasic = ref(false);
        const geoRecordImages = ref<any[]>([]);
        const geoRecordCategories = ref();
        const totalArea = ref();
        const totalLength = ref();
        const totalPerimeter = ref();
        const isAdmin_exportGeoJson =
            (store.state.accountPermissions != null &&
                store.state.accountPermissions.indexOf(
                    'canEnableAdministrator',
                ) !== -1) ||
            (store.state.masqueradeAccountPermissions != null &&
                store.state.masqueradeAccountPermissions.indexOf(
                    'canEnableAdministrator',
                ) !== -1);

        const geoRecord = ref({
            id: 0,
            title: '',
            notes: '',
            projectId: 0,
            isTask: false,
            accountId: store.getters.account.id,
            courseSchemaId: store.getters.currentCourseSchema.id,
            organizationId: store.getters.currentOrganization.id,
            images: [],
            geoRecordCategoryId: 0,
            geometry: {
                coordinates: [0, 0],
                type: 'Point',
            },
        } as GeoRecord);

        const responsiveOptions = [
            {
                breakpoint: '1500px',
                numVisible: 5,
            },
            {
                breakpoint: '1024px',
                numVisible: 3,
            },
            {
                breakpoint: '768px',
                numVisible: 2,
            },
            {
                breakpoint: '560px',
                numVisible: 1,
            },
        ];

        let geoRecordArr: any = [];

        // methods
        const convertArea = (area: number) => {
            area = area * (isMeters ? 1 : 1.19599);
            return area.toFixed(2) + (isMeters ? 'm²' : 'yd²');
        };

        const converLength = (length: number) => {
            length = length * (isMeters ? 1 : 1.09361);
            return length.toFixed(2) + (isMeters ? 'm' : 'yd');
        };

        const convertPerimeter = (perimeter: number) => {
            perimeter = perimeter * (isMeters ? 1 : 1.09361);
            return perimeter.toFixed(2) + (isMeters ? 'm' : 'yd');
        };

        const geoRecordAreaImprovement = (geoRecords: any): void => {
            geoRecords.forEach((element: any) => {
                if (element.area == 0) {
                    element.area = null;
                    return;
                }

                element.area = convertArea(element.area);
            });
        };

        const geoRecordLengthImprovement = (geoRecords: any): void => {
            geoRecords.forEach((element: any) => {
                if (element.length == 0) {
                    element.length = null;
                    return;
                }

                element.length = converLength(element.length);
            });
        };

        const geoRecordPerimeterImprovement = (geoRecords: any): void => {
            geoRecords.forEach((element: any) => {
                element.perimeter =
                    element.perimeter == 0
                        ? null
                        : convertPerimeter(element.perimeter);
            });
        };

        const getGeoRecords = async () => {
            geoRecordArr.value = await getAllGeoRecordSummary(
                store.getters.currentCourseSchema.id,
            );

            measurementImprovment(geoRecordArr.value);

            return geoRecordArr.value;
        };

        const sumArea = (geoRecords: any): void => {
            let area = geoRecords.reduce((sum, element) => {
                return sum + element.area;
            }, 0);

            totalArea.value = convertArea(area);
        };

        const sumLength = (geoRecords: any): void => {
            let length = geoRecords.reduce((sum, element) => {
                return sum + element.length;
            }, 0);

            totalLength.value = converLength(length);
        };

        const sumPerimeter = (geoRecords: any): void => {
            let perimeter = geoRecords.reduce((sum, element) => {
                return sum + element.perimeter;
            }, 0);

            totalPerimeter.value = convertPerimeter(perimeter);
        };

        const deleteGeoRecord = async (rowData: any) => {
            let geoRecord = {
                id: rowData,
            };
            if (rowData) {
                try {
                    await deleteGeoRecordLayer(props.mapbox.map, geoRecord);
                } catch (error) {
                    window.console.log(error);
                }
            }
        };

        const initNewNonGeometryGeoRecord = () => {
            geoRecord.value = {
                id: 0,
                title: '',
                notes: '',
                projectId: 0,
                isTask: false,
                accountId: store.getters.account.id,
                courseSchemaId: store.getters.currentCourseSchema.id,
                organizationId: store.getters.currentOrganization.id,
                images: [],
                geoRecordCategoryId: 0,
                geometry: {
                    coordinates: [0, 0],
                    type: 'Point',
                },
            } as GeoRecord;
        };

        const measurementImprovment = (geoRecords: any): void => {
            sumArea(geoRecords);
            sumLength(geoRecords);
            sumPerimeter(geoRecords);
            geoRecordAreaImprovement(geoRecords);
            geoRecordLengthImprovement(geoRecords);
            geoRecordPerimeterImprovement(geoRecords);
        };

        const updateCustomChange = (data) => {
            let column: any = [];
            let geoRecords = [];

            if (data.column) selectedColumns.value = data.column;

            if (data.filter) filters.value = data.filter;

            for (let i = 0; i < selectedColumns.value.length; i++) {
                column.push('' + selectedColumns.value[i].field);
            }

            geoRecords = getFilteredGeoRecords(
                geoRecordArr.value,
                filters.value,
                column,
            );

            unMarshallUnit(geoRecords);
            measurementImprovment(geoRecords);
        };

        const unMarshallUnit = (geoRecords): void => {
            geoRecords.map((geoRecord) => {
                geoRecord.area = geoRecord.area
                    ? parseFloat(geoRecord.area.replace(/[^0-9.]/g, ''))
                    : 0;

                geoRecord.length = geoRecord.length
                    ? parseFloat(geoRecord.length.replace(/[^0-9.]/g, ''))
                    : 0;

                geoRecord.perimeter = geoRecord.perimeter
                    ? parseFloat(geoRecord.perimeter.replace(/[^0-9.]/g, ''))
                    : 0;
            });
        };

        const generateGridPDF = async () => {
            if (geoRecordArr.value != null && geoRecordArr.value.length > 0) {
                try {
                    let result = geoRecordArr.value;
                    let column: any = [];

                    for (let i = 0; i < selectedColumns.value.length; i++) {
                        column.push('' + selectedColumns.value[i].field);
                    }

                    if (filters.value[column[0]]) {
                        result = getFilteredGeoRecords(
                            result,
                            filters.value,
                            column,
                        );
                    }

                    await generatePDf(result, column);

                    if (!isGeneratingPdf.value) {
                        successToast('PDF generated successfully');
                    }
                } catch (error) {
                    console.log(error);
                    isGeneratingPdf.value = false;
                    warnToast('PDF generation failed');
                }
            } else {
                isGeneratingPdf.value = false;
                errorToast('No Data to generate PDF');
            }
        };

        const getFilteredGeoRecords = (geoRecord, filters, column) => {
            let filteredArray: any = [];

            geoRecord.forEach((geoRecord) => {
                let isValid = true;

                column.forEach((column) => {
                    var columnVal = '' + geoRecord[column];
                    columnVal = columnVal.toLowerCase();
                    let contraints = filters[column].constraints;

                    contraints.forEach((constraints) => {
                        if (constraints.value != null) {
                            let matchMode = constraints.matchMode;
                            let value = '' + constraints.value.toLowerCase();
                            value = value.toLowerCase();

                            switch (matchMode) {
                                case 'startsWith':
                                    if (!columnVal.startsWith(value)) {
                                        isValid = false;
                                    }
                                    break;
                                case 'contains':
                                    if (!columnVal.includes(value)) {
                                        isValid = false;
                                    }
                                    break;
                                case 'notContains':
                                    if (columnVal.includes(value)) {
                                        isValid = false;
                                    }
                                    break;
                                case 'endsWith':
                                    if (!columnVal.endsWith(value)) {
                                        isValid = false;
                                    }
                                    break;
                                case 'notEquals':
                                    if (columnVal.match(value)) {
                                        isValid = false;
                                    }
                                    break;
                                case 'equals':
                                    if (!columnVal.match(value)) {
                                        isValid = false;
                                    }
                                    break;
                            }
                        }
                        if (isValid == false) {
                            return;
                        }
                    });
                    if (isValid == false) {
                        return;
                    }
                });
                if (isValid == true) {
                    filteredArray.push(geoRecord);
                }
            });
            return filteredArray;
        };

        const displayGeoRecordImage = async (rowData: any) => {
            let res = await getGeoRecordImagesById(rowData.id);
            let org = store.getters.currentOrganization.id;

            geoRecordImages.value = [];

            res.forEach((image, i) => {
                geoRecordImages.value.push({
                    itemImageSrc: `${apiUrl}/image/${org}/${image.filename}`,
                    thumbnailImageSrc: `${apiUrl}/image/${org}/${image.filename}`,
                    imageName: `${image.filename}`,
                    alt: i + 'image',
                    itemImageDate: new Date(
                        image.createDate,
                    ).toLocaleDateString('en-GB'),
                });
            });

            displayBasic.value = true;
        };

        const imageIconActions = {
            icon: 'pi pi-image',
            class: 'p-button-rounded p-button-text',
            onPress: displayGeoRecordImage,
        };

        const convertArrayToGeoJSON = (jsonArray) => {
            const features = jsonArray.map((item) => ({
                type: 'Feature',
                geometry: item.geometry,
                properties: {
                    id: item.id,
                    title: item.title,
                    isFill: item.isFill,
                    category: item.geoRecordCategory,
                    color: geoRecordCategories.value.find(
                        (category) => category.id === item.geoRecordCategoryId,
                    )
                        ? geoRecordCategories.value.find(
                              (category) =>
                                  category.id === item.geoRecordCategoryId,
                          ).color
                        : '#FFFFFF',
                    area: item.area,
                    height: item.height,
                    length: item.length,
                    tagName: item.tagName,
                },
            }));

            return {
                type: 'FeatureCollection',
                features: features,
            };
        };

        const exportGeoJson = () => {
            const geoJSON = convertArrayToGeoJSON(geoRecordArr.value);

            const jsonString = JSON.stringify(geoJSON, null, 2);

            let fileName =
                store.getters.currentCourseSchema.name + '_geojson.json';
            const blob = new Blob([jsonString], {
                type: 'application/json',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        };

        // lifecycle
        onMounted(async () => {
            geoRecordCategories.value = await getAllGeoRecordCategory();
        });

        onUnmounted(async () => {
            await props.refershArchiveGeoRecordLayerTree();
            await props.fetchArchiveEvents();
        });

        return {
            store,
            tableColumns,
            getGeoRecords,
            GeoRecordEditor,
            deleteGeoRecord,
            geoRecord,
            initNewNonGeometryGeoRecord,
            generateGridPDF,
            isGeneratingPdf,
            updateCustomChange,
            imageIconActions,
            displayBasic,
            geoRecordImages,
            responsiveOptions,
            totalArea,
            totalLength,
            totalPerimeter,
            exportGeoJson,
            isAdmin_exportGeoJson,
        };
    },
});
